import { useEffect, useRef, useCallback } from 'react';
import { trackJobFlowStep } from '../utils/jobFlowTracking';

/**
 * @typedef {Object} UseJobFlowTrackingOptions
 * @property {string}      flowSessionId - Session ID shared across all wizard steps (stable ref)
 * @property {string}      stepId        - Machine-readable step identifier
 * @property {string}      stepLabel     - Human-readable step label
 * @property {string|null} [jobId]       - Current job ID; may become available mid-step
 */

/**
 * @typedef {Object} UseJobFlowTrackingReturn
 * @property {function(string|null=): void} trackComplete - Call when step is successfully completed
 * @property {function(string|null=): void} trackAbandon  - Call when user explicitly leaves/cancels
 */

/**
 * Hook managing step-tracking lifecycle for the job-creation wizard.
 *
 * Behaviour:
 *  - Fires "started" once on mount (guarded against StrictMode double-invoke)
 *  - Fires "abandoned" on unmount only if "completed" was not already recorded
 *  - Provides trackComplete / trackAbandon for explicit calls
 *  - Calculates elapsed_ms automatically
 *  - Never blocks the UI
 *
 * @param {UseJobFlowTrackingOptions} options
 * @returns {UseJobFlowTrackingReturn}
 */
export function useJobFlowTracking({ flowSessionId, stepId, stepLabel, jobId = null }) {
    // Stable start timestamp; reset each time the component mounts
    const startTimeRef = useRef(Date.now());

    // Guards so "started" fires at most once per mount
    const hasTrackedStartRef = useRef(false);

    // Once either trackComplete or trackAbandon is called, prevent the other from firing
    const isSettledRef = useRef(false);

    // Always-current mirrors of props, safe to read inside cleanup closures
    const jobIdRef = useRef(jobId);
    const flowSessionIdRef = useRef(flowSessionId);

    useEffect(() => { jobIdRef.current = jobId; }, [jobId]);
    useEffect(() => { flowSessionIdRef.current = flowSessionId; }, [flowSessionId]);

    // ── "started" on mount ──────────────────────────────────────────────────
    useEffect(() => {
        if (hasTrackedStartRef.current || !flowSessionId) return;
        hasTrackedStartRef.current = true;
        startTimeRef.current = Date.now();

        trackJobFlowStep({
            flowSessionId,
            stepId,
            stepLabel,
            status: 'started',
            jobId: jobIdRef.current,
            elapsed_ms: 0,
        });
    // Intentional: only re-run if flowSessionId identity changes (never in practice)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flowSessionId]);

    // ── "abandoned" on unmount (if not already settled) ────────────────────
    useEffect(() => {
        return () => {
            if (isSettledRef.current) return;
            if (!flowSessionIdRef.current) return;

            trackJobFlowStep({
                flowSessionId: flowSessionIdRef.current,
                stepId,
                stepLabel,
                status: 'abandoned',
                jobId: jobIdRef.current,
                elapsed_ms: Date.now() - startTimeRef.current,
            });
        };
    // stepId / stepLabel are constants per component — safe to omit
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── trackComplete ────────────────────────────────────────────────────────
    const trackComplete = useCallback((overrideJobId) => {
        if (isSettledRef.current) return;
        isSettledRef.current = true;

        trackJobFlowStep({
            flowSessionId: flowSessionIdRef.current,
            stepId,
            stepLabel,
            status: 'completed',
            jobId: overrideJobId !== undefined ? overrideJobId : jobIdRef.current,
            elapsed_ms: Date.now() - startTimeRef.current,
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── trackAbandon (explicit) ──────────────────────────────────────────────
    const trackAbandon = useCallback((overrideJobId) => {
        if (isSettledRef.current) return;
        isSettledRef.current = true;

        trackJobFlowStep({
            flowSessionId: flowSessionIdRef.current,
            stepId,
            stepLabel,
            status: 'abandoned',
            jobId: overrideJobId !== undefined ? overrideJobId : jobIdRef.current,
            elapsed_ms: Date.now() - startTimeRef.current,
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { trackComplete, trackAbandon };
}
